import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import {
  Quote, Lightbulb, Target, Car, Bike, Trophy, ShieldAlert,
  MessageSquare, ExternalLink, ChevronLeft, ChevronRight,
  Sparkles, X, Pause, Play,
} from 'lucide-react';

/**
 * Widget « Actu de la vie & du fun » — Carrousel style Stories.
 * Mode : "public" (accueil visiteurs) | "student" (dashboard élève) | "teacher" (dashboard prof).
 */

const TYPE_META = {
  citation:         { icon: Quote,        label: 'Citation',       bg: 'from-amber-950/60 via-amber-900/40 to-[#0a0a0b]',  ring: 'ring-amber-500/40'  },
  saviez_vous:      { icon: Lightbulb,    label: 'Le saviez-vous', bg: 'from-sky-950/60 via-sky-900/40 to-[#0a0a0b]',      ring: 'ring-sky-500/40'    },
  defi:             { icon: Target,       label: 'Défi du jour',   bg: 'from-emerald-950/60 via-emerald-900/40 to-[#0a0a0b]', ring: 'ring-emerald-500/40' },
  actu_auto:        { icon: Car,          label: 'Actu auto',      bg: 'from-slate-800/60 via-slate-900/40 to-[#0a0a0b]',   ring: 'ring-slate-500/40'  },
  actu_moto:        { icon: Bike,         label: 'Actu moto',      bg: 'from-red-950/60 via-red-900/40 to-[#0a0a0b]',       ring: 'ring-red-500/40'    },
  sport_meca:       { icon: Trophy,       label: 'Sport méca',     bg: 'from-violet-950/60 via-violet-900/40 to-[#0a0a0b]', ring: 'ring-violet-500/40' },
  regle_officielle: { icon: ShieldAlert,  label: 'Règle officielle', bg: 'from-red-950/70 via-red-900/50 to-[#0a0a0b]',    ring: 'ring-red-600/50'    },
  post_enseignant:  { icon: MessageSquare, label: 'Ton enseignant', bg: 'from-blue-950/60 via-indigo-900/40 to-[#0a0a0b]',  ring: 'ring-blue-500/50'   },
};

const ACCENT_TEXT = {
  red: 'text-red-400',
  amber: 'text-amber-400',
  emerald: 'text-emerald-400',
  sky: 'text-sky-400',
  violet: 'text-violet-400',
};

function StoryCard({ item, onOpen, small = false }) {
  const meta = TYPE_META[item.type] || TYPE_META.citation;
  const Icon = meta.icon;
  const accentClr = ACCENT_TEXT[item.accent] || ACCENT_TEXT.red;

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`actu-card-${item.id}`}
      className={`group relative shrink-0 snap-start text-left overflow-hidden
        ${small ? 'w-[240px] h-[300px]' : 'w-[280px] sm:w-[320px] h-[380px] sm:h-[420px]'}
        rounded-3xl border border-white/10 bg-gradient-to-br ${meta.bg}
        ring-1 ${meta.ring} hover:ring-2 transition-all duration-300
        hover:scale-[1.02] hover:-translate-y-1 shadow-xl hover:shadow-2xl`}
    >
      {/* Grain texture */}
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '4px 4px' }} />

      {/* Header */}
      <div className="relative p-5 sm:p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${accentClr} bg-white/5 border border-white/10`}>
            <Icon size={12} strokeWidth={2.5} />
            {meta.label}
          </div>
          {item.ai_generated && (
            <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40">
              <Sparkles size={10} /> IA
            </div>
          )}
        </div>

        <h3 className={`font-serif-editorial text-white ${small ? 'text-[18px]' : 'text-[22px] sm:text-[26px]'} leading-tight mb-3`}>
          {item.title}
        </h3>

        <p className={`text-white/75 ${small ? 'text-[13px]' : 'text-[14px] sm:text-[15px]'} leading-relaxed flex-1 overflow-hidden`}
           style={{ display: '-webkit-box', WebkitLineClamp: small ? 5 : 7, WebkitBoxOrient: 'vertical' }}>
          {item.body}
        </p>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-[11px]">
          <span className="text-white/45">
            {item.author_type === 'teacher' ? `✍️ ${item.author_name || 'Ton enseignant'}` :
             item.author_type === 'ai' ? 'IA · Contenu du jour' :
             item.source_label ? `📖 ${item.source_label}` :
             'CodeVoiture'}
          </span>
          <span className={`inline-flex items-center gap-1 ${accentClr} font-semibold opacity-0 group-hover:opacity-100 transition-opacity`}>
            Lire <ChevronRight size={12} />
          </span>
        </div>
      </div>
    </button>
  );
}

function StoryModal({ item, onClose }) {
  const meta = TYPE_META[item.type] || TYPE_META.citation;
  const Icon = meta.icon;
  const accentClr = ACCENT_TEXT[item.accent] || ACCENT_TEXT.red;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="actu-story-modal"
    >
      <div
        className={`relative w-full max-w-lg rounded-3xl border border-white/15 bg-gradient-to-br ${meta.bg} ring-1 ${meta.ring} p-7 sm:p-9 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="actu-modal-close"
          className="absolute top-3 right-3 p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${accentClr} bg-white/5 border border-white/10 mb-5`}>
          <Icon size={12} strokeWidth={2.5} />
          {meta.label}
        </div>

        <h2 className="font-serif-editorial text-white text-[28px] sm:text-[34px] leading-tight mb-5">
          {item.title}
        </h2>

        <p className="text-white/85 text-[15px] sm:text-[17px] leading-relaxed whitespace-pre-line mb-6">
          {item.body}
        </p>

        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="actu-source-link"
            className="inline-flex items-center gap-2 text-[13px] font-semibold text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-4 py-2 transition"
          >
            <ExternalLink size={14} />
            Source officielle : {item.source_label || 'consulter'}
          </a>
        )}

        {item.author_type === 'teacher' && (
          <div className="mt-6 pt-5 border-t border-white/10 text-[12px] text-white/50">
            ✍️ Message publié par <strong className="text-white/80">{item.author_name}</strong>
          </div>
        )}
        {item.author_type === 'ai' && (
          <div className="mt-6 pt-5 border-t border-white/10 text-[12px] text-white/50 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Contenu généré par IA — vérifié sur sources officielles françaises quand mentionnées.
          </div>
        )}
      </div>
    </div>
  );
}

export default function ActuStoriesCarousel({ mode = 'public', title = 'L\'Actu & le fun de la conduite', subtitle }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [autoplay, setAutoplay] = useState(true);
  const scrollerRef = useRef(null);
  const autoplayTimerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = mode === 'student' ? api.actuFeedStudent : api.actuFeedPublic;
      const d = await fn(40);
      setItems(d.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  // Autoplay : défilement horizontal doux
  useEffect(() => {
    if (!autoplay || active || items.length < 2) return undefined;
    autoplayTimerRef.current = setInterval(() => {
      const el = scrollerRef.current;
      if (!el) return;
      const step = el.clientWidth * 0.75;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const next = el.scrollLeft + step;
      if (next >= maxScroll - 4) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    }, 5000);
    return () => clearInterval(autoplayTimerRef.current);
  }, [autoplay, active, items.length]);

  const scrollByStep = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10">
        <div className="h-[380px] rounded-3xl bg-white/[0.03] border border-white/10 animate-pulse" />
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10 sm:mt-14" data-testid="actu-stories-carousel">
      <div className="flex items-end justify-between mb-5 sm:mb-6 gap-4">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Nouveauté · Actu quotidienne
          </div>
          <h2 className="font-serif-editorial text-white text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.05]">
            {title}
          </h2>
          {subtitle && (
            <p className="text-white/55 text-sm sm:text-base mt-2 max-w-2xl">{subtitle}</p>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => setAutoplay((v) => !v)}
            data-testid="actu-autoplay-toggle"
            className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center transition"
            aria-label={autoplay ? 'Pause défilement' : 'Reprendre défilement'}
          >
            {autoplay ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            onClick={() => scrollByStep(-1)}
            data-testid="actu-scroll-prev"
            className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition"
            aria-label="Précédent"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scrollByStep(1)}
            data-testid="actu-scroll-next"
            className="w-10 h-10 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center transition"
            aria-label="Suivant"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="relative">
        {/* Fade edges */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#0a0a0b] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0a0a0b] to-transparent z-10" />

        <div
          ref={scrollerRef}
          className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 no-scrollbar"
          style={{ scrollbarWidth: 'none' }}
          data-testid="actu-scroller"
        >
          {items.map((it) => (
            <StoryCard key={it.id} item={it} onOpen={setActive} />
          ))}
        </div>
      </div>

      {active && <StoryModal item={active} onClose={() => setActive(null)} />}
    </section>
  );
}
